import React, { useMemo } from 'react';
import { TrendingUp, Users, Calendar, Clock, PieChart, BarChart3 } from 'lucide-react';
import { CATEGORIAS_AUSENCIAS, ESTADOS_AUSENCIAS } from '../../../api/absences';

export default function AbsenceStats({ absences, types, loading, currentUser }) {
  // Calcular estadísticas
  const stats = useMemo(() => {
    if (!absences || absences.length === 0) {
      return {
        porTipo: [],
        porEstado: [],
        porCategoria: [],
        totalDias: 0,
        empleadosAfectados: 0,
        tasaAprobacion: 0
      };
    }

    // Por tipo
    const porTipo = {};
    types.forEach((type) => {
      const count = absences.filter((a) => a.absence_type_id === type.id).length;
      if (count > 0) {
        porTipo[type.nombre] = count;
      }
    });

    // Por estado
    const porEstado = {};
    absences.forEach((abs) => {
      porEstado[abs.estado] = (porEstado[abs.estado] || 0) + 1;
    });

    // Por categoría
    const porCategoria = {};
    absences.forEach((abs) => {
      porCategoria[abs.tipo_categoria] = (porCategoria[abs.tipo_categoria] || 0) + 1;
    });

    // Total de días
    const totalDias = absences.reduce((sum, abs) => sum + (abs.dias_laborables || 0), 0);

    // Empleados únicos afectados
    const empleadosUnicos = new Set(absences.map((a) => a.employee_id));
    const empleadosAfectados = empleadosUnicos.size;

    // Tasa de aprobación
    const totalSolicitudes = absences.length;
    const aprobadas = absences.filter((a) => a.estado === 'aprobado').length;
    const tasaAprobacion =
      totalSolicitudes > 0 ? ((aprobadas / totalSolicitudes) * 100).toFixed(1) : 0;

    return {
      porTipo,
      porEstado,
      porCategoria,
      totalDias,
      empleadosAfectados,
      tasaAprobacion
    };
  }, [absences, types]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (absences.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <PieChart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">No hay datos suficientes para mostrar estadísticas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{absences.length}</span>
          </div>
          <p className="text-blue-100 text-sm">Total de Solicitudes</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.tasaAprobacion}%</span>
          </div>
          <p className="text-green-100 text-sm">Tasa de Aprobación</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.totalDias}</span>
          </div>
          <p className="text-purple-100 text-sm">Días Totales</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.empleadosAfectados}</span>
          </div>
          <p className="text-orange-100 text-sm">Empleados Afectados</p>
        </div>
      </div>

      {/* Distribución por Estado */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Distribución por Estado
        </h3>
        <div className="space-y-3">
          {Object.entries(stats.porEstado).map(([estado, count]) => {
            const config = ESTADOS_AUSENCIAS[estado] || ESTADOS_AUSENCIAS.pendiente;
            const percentage = ((count / absences.length) * 100).toFixed(1);

            return (
              <div key={estado}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-medium ${config.textColor}`}>{config.label}</span>
                  <span className="text-sm text-gray-600">
                    {count} ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`${config.bgColor.replace('100', '500')} h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribución por Categoría */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5" />
          Distribución por Categoría
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(stats.porCategoria).map(([categoria, count]) => {
            const config = CATEGORIAS_AUSENCIAS[categoria] || CATEGORIAS_AUSENCIAS.permiso;
            const percentage = ((count / absences.length) * 100).toFixed(1);

            return (
              <div
                key={categoria}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="text-4xl">{config.icon}</div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{config.label}</p>
                  <p className="text-sm text-gray-600">
                    {count} solicitudes ({percentage}%)
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Distribución por Tipo */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Top Tipos de Ausencias
        </h3>
        <div className="space-y-3">
          {Object.entries(stats.porTipo)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([tipo, count]) => {
              const percentage = ((count / absences.length) * 100).toFixed(1);

              return (
                <div key={tipo}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-700">{tipo}</span>
                    <span className="text-sm text-gray-600">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-orange-400 to-pink-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Promedio de Días */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Métricas de Duración
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-gray-600 text-sm mb-1">Promedio de días por ausencia</p>
            <p className="text-3xl font-bold text-indigo-600">
              {absences.length > 0
                ? (stats.totalDias / absences.length).toFixed(1)
                : 0}
            </p>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Ausencia más larga</p>
            <p className="text-3xl font-bold text-purple-600">
              {Math.max(...absences.map((a) => a.dias_laborables || 0), 0)} días
            </p>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Ausencia más corta</p>
            <p className="text-3xl font-bold text-pink-600">
              {Math.min(
                ...absences.filter((a) => a.dias_laborables > 0).map((a) => a.dias_laborables),
                0
              )}{' '}
              días
            </p>
          </div>
        </div>
      </div>

      {/* Top Empleados */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Empleados con Más Ausencias
        </h3>
        <div className="space-y-3">
          {Object.entries(
            absences.reduce((acc, abs) => {
              if (!acc[abs.employee_id]) {
                acc[abs.employee_id] = {
                  nombre: abs.empleado_nombre,
                  count: 0,
                  dias: 0
                };
              }
              acc[abs.employee_id].count++;
              acc[abs.employee_id].dias += abs.dias_laborables || 0;
              return acc;
            }, {})
          )
            .sort((a, b) => b[1].count - a[1].count)
            .slice(0, 5)
            .map(([employeeId, data]) => (
              <div
                key={employeeId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-800">{data.nombre}</p>
                  <p className="text-sm text-gray-600">
                    {data.count} solicitudes · {data.dias} días
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">{data.count}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
